import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunk-error';

interface Props {
  children: ReactNode;
  /** The error is cleared when this changes (AppLayout passes the pathname). */
  resetKey?: string;
}

interface State {
  error: Error | null;
  /** Whether a chunk-load error triggered an automatic reload (null until decided). */
  reloading: boolean | null;
}

/**
 * Catches a render crash of the page under AppLayout's `<Outlet />` and
 * shows a French message in its place; the sidebar and header stay usable.
 * A chunk-load error from a stale deploy reloads the page (chunk-error.ts).
 */
export class PageErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reloading: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      this.setState({ reloading: reloadOnceForChunkError() });
      return;
    }
    console.error('Erreur de rendu de page :', error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, reloading: null });
    }
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;

    if (isChunkLoadError(error) && reloading !== false) {
      return (
        <div className="flex min-h-[50vh] items-center justify-center text-sm text-slate-500">
          Mise à jour de l'application…
        </div>
      );
    }

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <h2 className="text-lg font-semibold text-slate-900">
          Une erreur est survenue sur cette page
        </h2>
        <p className="max-w-md text-sm text-slate-500">
          Le reste de l'application reste utilisable. Réessayez, ou revenez à
          l'accueil si le problème persiste.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Réessayer
          </button>
          <Link
            to="/"
            className="rounded-lg bg-brand-lagune-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-lagune-800"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }
}
